import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, FileText, DollarSign, Package, ArrowRight, Tag, Users, AlertCircle } from 'lucide-react';
import { STATUS_META } from '../../data/adminMockData';
import { useStore } from '../../context/StoreContext';
export function AdminDashboard() {
  const {
    orders,
    customers,
    discounts
  } = useStore();
  const validOrders = orders.filter(o => o.status !== 'cancelled' && o.status !== 'refunded');
  const totalRevenue = validOrders.reduce((sum, o) => sum + o.items.reduce((s, i) => s + i.unitPrice * i.qty + (i.protection?.price || 0), 0) + o.shippingCost + o.tax, 0);
  const orderCount = orders.length;
  const aov = orderCount > 0 ? totalRevenue / validOrders.length : 0;
  const protectionAttachRate = orderCount > 0 ? orders.filter(o => o.items.some(i => i.protection)).length / orderCount * 100 : 0;
  const discountUsage = orders.filter(o => o.notes && o.notes.includes('Discount applied')).length;
  const newCustomers = customers.length;
  const needsFulfillment = orders.filter(o => o.status === 'paid').length;
  const stats = [{
    label: 'Total Orders',
    value: orderCount.toString(),
    icon: ShoppingBag
  }, {
    label: 'Revenue',
    value: `${totalRevenue.toLocaleString(undefined, {
      maximumFractionDigits: 0
    })}`,
    icon: DollarSign
  }, {
    label: 'Avg Order Value',
    value: `${aov.toLocaleString(undefined, {
      maximumFractionDigits: 0
    })}`,
    icon: DollarSign
  }, {
    label: 'YarboCare Attach Rate',
    value: `${protectionAttachRate.toFixed(0)}%`,
    icon: Package
  }, {
    label: 'Discount Usage',
    value: discountUsage.toString(),
    icon: Tag
  }, {
    label: 'New Customers',
    value: newCustomers.toString(),
    icon: Users
  }, {
    label: 'Needs Fulfillment',
    value: needsFulfillment.toString(),
    icon: AlertCircle,
    highlight: needsFulfillment > 0
  }];
  const recent = [...orders].slice(0, 5);
  return <div data-id="element-492">
      <h1 className="text-2xl font-extrabold text-yarbo-dark mb-1" data-id="element-493">
        Dashboard
      </h1>
      <p className="text-sm text-gray-600 mb-6" data-id="element-494">
        Quick view of mock data for dev/design review only.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-495">
        {stats.map(s => <div key={s.label} className={`bg-white border rounded-xl p-5 ${s.highlight ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`} data-id="element-496">
            <div className="flex items-center justify-between mb-3" data-id="element-497">
              <span className={`text-xs font-bold uppercase tracking-wider ${s.highlight ? 'text-amber-800' : 'text-gray-500'}`} data-id="element-498">
                {s.label}
              </span>
              <s.icon className={`w-4 h-4 ${s.highlight ? 'text-amber-500' : 'text-gray-400'}`} data-id="element-499" />
            </div>
            <div className={`text-2xl font-extrabold ${s.highlight ? 'text-amber-900' : 'text-yarbo-dark'}`} data-id="element-500">
              {s.value}
            </div>
          </div>)}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-501">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-502">
          <h2 className="text-sm font-extrabold text-yarbo-dark" data-id="element-503">
            Recent Orders
          </h2>
          <Link to="/admin/orders" className="text-xs font-bold text-yarbo-dark hover:text-black inline-flex items-center" data-id="element-504">
            View all <ArrowRight className="w-3 h-3 ml-1" data-id="element-505" />
          </Link>
        </div>
        <ul className="divide-y divide-gray-100" data-id="element-506">
          {recent.map(o => {
          const meta = STATUS_META[o.status];
          const total = o.items.reduce((s, i) => s + i.unitPrice * i.qty + (i.protection?.price || 0), 0) + o.shippingCost + o.tax;
          return <li key={o.id} data-id="element-507">
                <Link to={`/admin/sales?focus=${o.id}`} className="block px-5 py-3 hover:bg-gray-50" data-id="element-508">
                  <div className="flex items-center justify-between gap-3" data-id="element-509">
                    <div className="min-w-0" data-id="element-510">
                      <div className="text-sm font-bold text-yarbo-dark" data-id="element-511">
                        {o.id}
                      </div>
                      <div className="text-xs text-gray-500 truncate" data-id="element-512">
                        {o.customer.firstName} {o.customer.lastName} ·{' '}
                        {new Date(o.placedAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0" data-id="element-513">
                      <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 ${meta.bg} ${meta.text}`} data-id="element-514">
                        {meta.label}
                      </span>
                      <span className="text-sm font-extrabold text-yarbo-dark" data-id="element-515">
                        $
                        {total.toLocaleString(undefined, {
                      maximumFractionDigits: 0
                    })}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>;
        })}
        </ul>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-516">
        <Link to="/admin/sales" className="bg-white border border-gray-200 rounded-xl p-5 hover:border-yarbo-dark hover:shadow-md transition-all flex items-start gap-4" data-id="element-517">
          <ShoppingBag className="w-8 h-8 text-yarbo-yellow bg-yarbo-dark rounded-lg p-1.5" data-id="element-518" />
          <div data-id="element-519">
            <h3 className="font-bold text-yarbo-dark" data-id="element-520">Sales</h3>
            <p className="text-sm text-gray-600" data-id="element-521">
              View orders with full customer info, items, payment, and
              protection plans.
            </p>
          </div>
        </Link>
        <Link to="/admin/templates" className="bg-white border border-gray-200 rounded-xl p-5 hover:border-yarbo-dark hover:shadow-md transition-all flex items-start gap-4" data-id="element-522">
          <FileText className="w-8 h-8 text-yarbo-yellow bg-yarbo-dark rounded-lg p-1.5" data-id="element-523" />
          <div data-id="element-524">
            <h3 className="font-bold text-yarbo-dark" data-id="element-525">Templates</h3>
            <p className="text-sm text-gray-600" data-id="element-526">
              Email and SMS templates fired by order events. Variables
              supported.
            </p>
          </div>
        </Link>
      </div>
    </div>;
}